// The stake-currency filter (SM-490) — "show me the positions held in
// one currency", and the rule about which currencies that question can
// name.
//
// A position is exposure that somebody PAID for, and what they paid in
// is the channel policy's debit leg: a CASH channel debits GOLD, a GAME
// channel debits RED. That is the currency the row's chip shows and the
// one an operator reconciles a wallet against, so it is the one the
// filter narrows by — the filter and the column must answer the same
// question or the table contradicts the control above it.
//
// GREEN is therefore absent, and the reason is shown rather than left
// to be inferred: it is a payout leg only (GAME credits GREEN on sell,
// redeem and resolution), so no exposure is ever OPENED in it and the
// option could only ever return an empty page. Same posture as
// <CurrencySelect>'s excluded actions — an absent option with no
// explanation is indistinguishable from a bug.

import { CurrencyFilterSelect } from "@/components/currency/CurrencyFilterSelect";
import { type CurrencyCode } from "@/currency";

/**
 * The currencies exposure can be STAKED in, in the order the currency
 * registry lists them. Mirrors settlement-service's
 * vocab.ModeForDebitCurrency: one mode debits each, and nothing debits
 * GREEN.
 */
export const STAKE_CURRENCIES = ["RED", "GOLD"] as const satisfies readonly CurrencyCode[];

export type StakeCurrency = (typeof STAKE_CURRENCIES)[number];

/**
 * Why GREEN is not on the list, in the words of the page asking.
 *
 * Two whole sentences rather than one template with the subject
 * interpolated, and the duplication is the point twice over:
 *
 *  - The same fact has a different subject on each list — a position is
 *    HELD, an order is PLACED — and a note that talks about positions
 *    above a table of orders reads as copy pasted from somewhere else,
 *    which is what it would be.
 *  - `scripts/i18n-page-copy.mjs` extracts literals. A composed string
 *    reaches the catalog as "… so {{value1}}." and the clause that
 *    carries the meaning never gets translated at all.
 *
 * Exported rather than rendered as the Select's `hint`: this control
 * lives in a filter BAR whose items are bottom-aligned, and a two-line
 * note under one of them drags every neighbour down with it. The page
 * gives it its own line under the bar instead.
 */

/** Active Positions (SM-490). */
export const NON_STAKE_CURRENCY_HINT =
  "GREEN is a payout currency — GAME exposure is staked in RED and paid out in GREEN — so no position is held in it.";

/** Active Orders and Order History (SM-511). */
export const NON_STAKE_ORDER_CURRENCY_HINT =
  "GREEN is a payout currency — GAME exposure is staked in RED and paid out in GREEN — so no order is ever placed in it.";

/**
 * Read a stake currency out of untrusted text (a URL search param).
 *
 * Anything else — GREEN, a typo, a code that does not exist — reads as
 * NO filter, because this value drives a control the operator can see:
 * a URL the picker cannot represent would leave the table filtered and
 * the picker saying "All currencies". The server keeps its own
 * fail-closed guard for callers that are not this page, where an
 * unrecognised currency selects nothing rather than everything.
 */
export function parseStakeCurrency(raw: unknown): StakeCurrency | undefined {
  return typeof raw === "string" && (STAKE_CURRENCIES as readonly string[]).includes(raw)
    ? (raw as StakeCurrency)
    : undefined;
}

/**
 * The picker. Options carry the currency's CONFIGURED name and mark —
 * whatever an operator set on /currencies — so the list reads the same
 * as the chips in the table below it rather than as three wire codes.
 *
 * That rendering is <CurrencyFilterSelect>'s (SM-494), shared with the
 * other lists that narrow by currency; what stays here is the rule this
 * control exists for — WHICH currencies a position can be staked in.
 * "All currencies" carries the empty value because this page's default
 * IS no filter: an absent search param and the All row mean the same
 * thing.
 */
export function StakeCurrencyFilter({
  value,
  onChange,
  label = "Currency",
  placeholder = "All currencies",
  className,
}: {
  value: StakeCurrency | undefined;
  onChange: (next: StakeCurrency | undefined) => void;
  label?: string;
  placeholder?: string;
  className?: string;
}) {
  return (
    <CurrencyFilterSelect
      codes={STAKE_CURRENCIES}
      value={value}
      onChange={onChange}
      label={label}
      allLabel={placeholder}
      className={className}
    />
  );
}
